import React, { useState, useEffect, useCallback } from 'react';
import { Search, Users, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { LoadingState } from '@/components/ui/loading-state';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/contexts/unified-permissions';
import { getAllUsersForPicker } from '../services/chatService';
import type { ChatParticipant } from '../types';

interface UserPickerProps {
  onUserSelect: (userId: string) => void;
  className?: string;
}

/**
 * User picker component for admins to initiate conversations
 * Shows a searchable list of all users in a dialog
 */
export const UserPicker: React.FC<UserPickerProps> = ({
  onUserSelect,
  className,
}) => {
  const { user } = usePermissions();
  const [isOpen, setIsOpen] = useState(false);
  const [users, setUsers] = useState<ChatParticipant[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<ChatParticipant[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchUsers = useCallback(async () => {
    if (!user?.id) return;

    setLoading(true);
    setError(null);

    try {
      const fetchedUsers = await getAllUsersForPicker(user.id);
      setUsers(fetchedUsers);
    } catch (err) {
      const errorMessage = err instanceof Error ? err : new Error('Failed to load users');
      console.error('Error fetching users:', errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Fetch users when dialog opens
  useEffect(() => {
    if (isOpen && user?.id) {
      fetchUsers();
    }
  }, [isOpen, user?.id, fetchUsers]);

  // Filter users based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = users.filter((user) => {
      const fullName = user.fullName?.toLowerCase() || '';
      const username = user.username?.toLowerCase() || '';
      return fullName.includes(query) || username.includes(query);
    });

    setFilteredUsers(filtered);
  }, [users, searchQuery]);



  const handleUserSelect = (userId: string) => {
    onUserSelect(userId);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearchQuery('');
      setError(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className={cn('gap-2', className)}>
          <Plus className="h-4 w-4" />
          Start New Chat
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Start New Conversation
          </DialogTitle>
          <DialogDescription>
            Select a user to start a new conversation with them.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Error state */}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>
                Failed to load users: {error.message}
              </AlertDescription>
            </Alert>
          )}

          {/* Loading state */}
          {loading && (
            <div className="flex items-center justify-center py-8">
              <LoadingState description="Loading users..." />
            </div>
          )}

          {/* Users list */}
          {!loading && !error && (
            <ScrollArea className="h-[300px]">
              <div className="space-y-1">
                {filteredUsers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    {searchQuery ? 'No users found matching your search.' : 'No users available.'}
                  </div>
                ) : (
                  filteredUsers.map((user) => (
                    <UserPickerItem
                      key={user.id}
                      user={user}
                      onSelect={handleUserSelect}
                    />
                  ))
                )}
              </div>
            </ScrollArea>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Individual user item in the picker
 */
interface UserPickerItemProps {
  user: ChatParticipant;
  onSelect: (userId: string) => void;
}

const UserPickerItem: React.FC<UserPickerItemProps> = ({ user, onSelect }) => {
  const displayName = user.fullName || user.username || 'Unknown User';
  const secondaryText = user.username && user.fullName ? `@${user.username}` : '';

  const handleClick = () => {
    onSelect(user.id);
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
        'hover:bg-muted/50 hover:border-primary/20'
      )}
    >
      {/* Avatar placeholder */}
      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
        <span className="text-sm font-medium text-muted-foreground">
          {displayName.charAt(0).toUpperCase()}
        </span>
      </div>

      {/* User info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{displayName}</p>
        {secondaryText && (
          <p className="text-xs text-muted-foreground truncate">{secondaryText}</p>
        )}
      </div>
    </div>
  );
};
